/**
 * Core Event Bus Module for Murphy Door Configurator
 *
 * Provides centralized event management by:
 * - Managing event subscriptions and publications
 * - Handling event propagation
 * - Providing error handling for event callbacks
 * - Supporting event batching and throttling
 * - Maintaining event history for debugging
 * - Monitoring performance metrics
 * - Freezing ALL event payloads centrally (Pattern #18 DRY - single freeze point)
 *
 * Dependencies:
 * - None (this is a core module)
 */

// Performance threshold constants (CONSTANTS.EVENTS references in comments for traceability)
const PERFORMANCE_THRESHOLDS = Object.freeze({
  MAX_EVENT_RATE: 50, // events/second. Threshold for CONSTANTS.EVENTS.PERFORMANCE_HIGH_FREQUENCY events.
  MAX_EXECUTION_TIME: 100, // milliseconds. Threshold for CONSTANTS.EVENTS.PERFORMANCE_EVENT_SLOW on a whole event.
  MAX_MEMORY_USAGE: 50 * 1024 * 1024, // 50MB in bytes. Threshold for CONSTANTS.EVENTS.PERFORMANCE_MEMORY_SPIKE.
  SLOW_EVENT_THRESHOLD: 150, // milliseconds. Threshold for a single callback to be considered slow.
  METRICS_UPDATE_INTERVAL: 5000, // milliseconds. How often to publish CONSTANTS.EVENTS.PERFORMANCE_METRICS_UPDATED.
  HIGH_FREQUENCY_CHECK_INTERVAL: 500, // milliseconds. How often to check for high frequency events.
});

// Create a module scope for testing
const createCoreEventBus = (context) => {
  const subscribers = new Map();
  let eventHistory = [];
  let uiEventQueue = [];
  let initialized = false; // 🔒 Track if initialize() has ever been called (prevents ANY re-initialization)
  let bootstrapComplete = false; // 🚀 Track bootstrap state for runtime protection
  const performanceMetrics = {
    subscriberCount: 0,
    memoryUsage: 0,
    eventCounts: {}, // Track counts by event type
    executionTimes: {}, // Track execution times by event type
    slowEvents: [], // Track slow events
    highFrequencyEvents: [], // Track high frequency events
    highFrequencyThreshold: PERFORMANCE_THRESHOLDS.MAX_EVENT_RATE,
    lastHighFrequencyCheck: Date.now(), // Initialize with current time
    maxEventRate: PERFORMANCE_THRESHOLDS.MAX_EVENT_RATE,
    maxExecutionTime: PERFORMANCE_THRESHOLDS.MAX_EXECUTION_TIME,
    maxMemoryUsage: PERFORMANCE_THRESHOLDS.MAX_MEMORY_USAGE,
    lastMetricsUpdate: 0,
  };

  // Maximum number of events to keep in history
  const MAX_HISTORY_LENGTH = 100;

  // Dev-only logging (production guard)
  const isDev = () =>
    context?.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  /**
   * Event type constants for CoreEventBus internal events
   * Prevents magic strings and provides clear event inventory
   * Pattern from: md-config-bl-material-selector.js
   */
  // ✅ MODERNIZED: Use centralized CONSTANTS.EVENTS (with fallback for bootstrap safety)
  const _CONSTANTS = context?.MurphyDoorConfigurator?.CONSTANTS;
  const EVENT_TYPES = _CONSTANTS
    ? {
        BOOTSTRAP_COMPLETE: _CONSTANTS.EVENTS.SYSTEM_BOOTSTRAP_COMPLETE,
        EVENT_BUS_INITIALIZED: _CONSTANTS.EVENTS.SYSTEM_EVENT_BUS_INITIALIZED,
        DOM_READY: _CONSTANTS.EVENTS.SYSTEM_DOM_READY,
        JQUERY_MISSING: _CONSTANTS.EVENTS.SYSTEM_JQUERY_MISSING,
        ERROR: _CONSTANTS.EVENTS.SYSTEM_ERROR,
        HISTORY_CLEARED: _CONSTANTS.EVENTS.SYSTEM_HISTORY_CLEARED,
        STATE_CLEARED: _CONSTANTS.EVENTS.SYSTEM_STATE_CLEARED,
        UI_QUEUE_PROCESSED: _CONSTANTS.EVENTS.SYSTEM_UI_QUEUE_PROCESSED,
        EVENT_SLOW: _CONSTANTS.EVENTS.PERFORMANCE_EVENT_SLOW,
        HIGH_FREQUENCY: _CONSTANTS.EVENTS.PERFORMANCE_HIGH_FREQUENCY,
        MEMORY_SPIKE: _CONSTANTS.EVENTS.PERFORMANCE_MEMORY_SPIKE,
        METRICS_UPDATED: _CONSTANTS.EVENTS.PERFORMANCE_METRICS_UPDATED,
        CONFIG_UPDATED: _CONSTANTS.EVENTS.PERFORMANCE_CONFIG_UPDATED,
      }
    : {
        // Fallback: raw strings if CONSTANTS not yet available (CONSTANTS.EVENTS equivalents)
        BOOTSTRAP_COMPLETE: 'system:bootstrap_complete', // CONSTANTS.EVENTS.SYSTEM_BOOTSTRAP_COMPLETE
        EVENT_BUS_INITIALIZED: 'system:event_bus_initialized', // CONSTANTS.EVENTS.SYSTEM_EVENT_BUS_INITIALIZED
        DOM_READY: 'system:dom_ready', // CONSTANTS.EVENTS.SYSTEM_DOM_READY
        JQUERY_MISSING: 'system:jquery_missing', // CONSTANTS.EVENTS.SYSTEM_JQUERY_MISSING
        ERROR: 'system:error', // CONSTANTS.EVENTS.SYSTEM_ERROR
        HISTORY_CLEARED: 'system:history_cleared', // CONSTANTS.EVENTS.SYSTEM_HISTORY_CLEARED
        STATE_CLEARED: 'system:state_cleared', // CONSTANTS.EVENTS.SYSTEM_STATE_CLEARED
        UI_QUEUE_PROCESSED: 'system:ui_queue_processed', // CONSTANTS.EVENTS.SYSTEM_UI_QUEUE_PROCESSED
        EVENT_SLOW: 'performance:event_slow', // CONSTANTS.EVENTS.PERFORMANCE_EVENT_SLOW
        HIGH_FREQUENCY: 'performance:high_frequency', // CONSTANTS.EVENTS.PERFORMANCE_HIGH_FREQUENCY
        MEMORY_SPIKE: 'performance:memory_spike', // CONSTANTS.EVENTS.PERFORMANCE_MEMORY_SPIKE
        METRICS_UPDATED: 'performance:metrics_updated', // CONSTANTS.EVENTS.PERFORMANCE_METRICS_UPDATED
        CONFIG_UPDATED: 'performance:config_updated', // CONSTANTS.EVENTS.PERFORMANCE_CONFIG_UPDATED
      };

  const validateEventType = (eventType) => {
    // Check if it's a string first
    if (typeof eventType !== 'string') {
      throw new Error('Event type must be a string');
    }

    // Then check the prefix against all valid prefixes
    const validPrefixes = [
      'system:',
      'ui:',
      'state:',
      'business:',
      'analytics:',
      'performance:',
      'test:',
      'error:',
      'recovery:',
      'selection:',
      'data:',
      'price:',
      'persistence:',
      'validation:',
      'material:',
      'conductor:',
      'restoration:',
      'forbidden:',
      'ppd:',
      'module:',
      'debug:',
      'support:', // For support modules: ProgressTracker, ValidationManager, SortingManager, etc.
      'klaviyo:', // For Klaviyo integration events: variant_intercept, mageworx_intercept, mapping_ready
    ];

    if (!validPrefixes.some((prefix) => eventType.startsWith(prefix))) {
      throw new Error('Invalid event type prefix');
    }
  };

  const isDOMReady = () => {
    return (
      context.document.readyState === 'complete' ||
      context.document.readyState === 'interactive'
    );
  };

  const isJQueryAvailable = () => {
    return typeof context.jQuery !== 'undefined';
  };

  const trackPerformance = (eventType, startTime, coreEventBusInstance) => {
    try {
      const now = Date.now();
      const executionTime = (context.performance?.now?.() || now) - startTime;

      // Skip performance tracking for performance events to prevent recursion
      if (eventType.startsWith('performance:')) return;

      // Track execution time
      if (!performanceMetrics.executionTimes[eventType]) {
        performanceMetrics.executionTimes[eventType] = [];
      }
      const times = performanceMetrics.executionTimes[eventType];
      times.push(executionTime);

      // Keep only last 100 execution times
      if (times.length > 100) {
        times.shift();
      }

      // Calculate average execution time
      const avgExecutionTime = times.reduce((a, b) => a + b, 0) / times.length;

      // Track event frequency - ensure the eventCounts object is initialized
      if (!performanceMetrics.eventCounts[eventType]) {
        performanceMetrics.eventCounts[eventType] = 0;
      }

      // Increment the count
      performanceMetrics.eventCounts[eventType]++;

      // Check for high frequency events - only if it's been at least 500ms since last check
      const timeSinceLastCheck =
        now - performanceMetrics.lastHighFrequencyCheck;

      if (
        timeSinceLastCheck >=
        PERFORMANCE_THRESHOLDS.HIGH_FREQUENCY_CHECK_INTERVAL
      ) {
        // Check each event type for high frequency
        const eventTypes = Object.keys(performanceMetrics.eventCounts);

        for (const type of eventTypes) {
          const count = performanceMetrics.eventCounts[type];
          // Calculate events per second using the time since last check
          const eventsPerSecond = (count / timeSinceLastCheck) * 1000;

          // Check if this event type exceeds the threshold
          if (eventsPerSecond > performanceMetrics.maxEventRate) {
            // Record the high frequency event
            performanceMetrics.highFrequencyEvents.push({
              eventType: type,
              eventsPerSecond,
              timestamp: now,
            });

            // Trim to last 50 events
            if (performanceMetrics.highFrequencyEvents.length > 50) {
              performanceMetrics.highFrequencyEvents.shift();
            }

            // Publish the high frequency event
            coreEventBusInstance.publish(EVENT_TYPES.HIGH_FREQUENCY, {
              eventType: type,
              eventsPerSecond,
              threshold: performanceMetrics.maxEventRate,
              source: 'system',
              timestamp: now,
            });
          }
        }

        // Reset all counters after check
        performanceMetrics.eventCounts = {};
        performanceMetrics.lastHighFrequencyCheck = now;
      }

      // Check for slow events
      if (executionTime > performanceMetrics.maxExecutionTime) {
        performanceMetrics.slowEvents.push({
          eventType,
          executionTime,
          averageExecutionTime: avgExecutionTime,
          timestamp: now,
        });

        // Keep only last 50 slow events
        if (performanceMetrics.slowEvents.length > 50) {
          performanceMetrics.slowEvents.shift();
        }

        coreEventBusInstance.publish(EVENT_TYPES.EVENT_SLOW, {
          eventType,
          executionTime,
          averageExecutionTime: avgExecutionTime,
          threshold: performanceMetrics.maxExecutionTime,
          source: 'system',
          timestamp: now,
        });
      }

      // Track memory usage
      const memoryUsage = context.performance?.memory?.usedJSHeapSize || 0;
      if (memoryUsage > performanceMetrics.maxMemoryUsage) {
        coreEventBusInstance.publish(EVENT_TYPES.MEMORY_SPIKE, {
          currentUsage: memoryUsage,
          threshold: performanceMetrics.maxMemoryUsage,
          delta: memoryUsage - performanceMetrics.memoryUsage,
          source: 'system',
          timestamp: now,
        });
      }
      performanceMetrics.memoryUsage = memoryUsage;

      // Publish periodic performance metrics
      const timeSinceLastMetrics = now - performanceMetrics.lastMetricsUpdate;
      if (
        timeSinceLastMetrics >= PERFORMANCE_THRESHOLDS.METRICS_UPDATE_INTERVAL
      ) {
        // Every 5 seconds
        coreEventBusInstance.publish(EVENT_TYPES.METRICS_UPDATED, {
          metrics: {
            executionTimes: performanceMetrics.executionTimes,
            eventCounts: performanceMetrics.eventCounts,
            slowEvents: performanceMetrics.slowEvents,
            highFrequencyEvents: performanceMetrics.highFrequencyEvents,
            memoryUsage: performanceMetrics.memoryUsage,
            subscriberCount: performanceMetrics.subscriberCount,
          },
          source: 'system',
          timestamp: now,
        });
        performanceMetrics.lastMetricsUpdate = now;
      }
    } catch (error) {
      coreEventBusInstance.publish(EVENT_TYPES.ERROR, {
        error: error.message,
        stack: error.stack,
        source: 'system',
        timestamp: Date.now(),
      });
    }
  };

  /**
   * Handle errors in event handlers
   * @private
   */
  const handleError = (error, eventType, eventData) => {
    // Don't track errors for error events to prevent recursion
    if (eventType.startsWith('error:')) return;

    // Null check: CoreEventBus may not be defined yet (window.MurphyDoorConfigurator.CoreEventBus)
    if (CoreEventBus) {
      CoreEventBus.publish(
        _CONSTANTS?.EVENTS?.ERROR_EVENT_HANDLER || 'error:event_handler',
        {
          // CONSTANTS.EVENTS fallback
          error: error.message,
          stack: error.stack,
          eventType,
          eventData: JSON.stringify(eventData),
          timestamp: Date.now(),
          source: 'system',
          severity: 'error',
          context: {
            subscriberCount: performanceMetrics.subscriberCount,
            memoryUsage: performanceMetrics.memoryUsage,
            lastEventType: eventType,
          },
        }
      );
    }
  };

  /**
   * Execute event callbacks with performance tracking
   * @private
   */
  const executeCallbacks = (eventType, eventData) => {
    if (!subscribers.has(eventType)) return;

    const eventSubscribers = subscribers.get(eventType);
    let callbackIndex = 0;

    eventSubscribers.forEach((callback) => {
      try {
        const startTime = context.performance?.now?.() || Date.now();
        callback(eventData);
        const executionTime =
          (context.performance?.now?.() || Date.now()) - startTime;

        // Track slow callbacks (over threshold)
        const isSlowCallback =
          executionTime > PERFORMANCE_THRESHOLDS.SLOW_EVENT_THRESHOLD &&
          !eventType.startsWith('performance:');
        if (isSlowCallback && CoreEventBus) {
          CoreEventBus.publish(EVENT_TYPES.EVENT_SLOW, {
            eventType,
            executionTime,
            callbackIndex, // NEW: Identify which callback was slow
            totalCallbacks: eventSubscribers.size, // NEW: Total number of callbacks
            averageExecutionTime:
              performanceMetrics.executionTimes[eventType]?.reduce(
                (a, b) => a + b,
                0
              ) / (performanceMetrics.executionTimes[eventType]?.length || 1),
            source: 'system',
            timestamp: Date.now(),
          });
        }

        callbackIndex++;
      } catch (error) {
        handleError(error, eventType, eventData);
      }
    });
  };

  // Core Event Bus implementation
  const CoreEventBus = {
    /**
     * Initialize the event bus
     * @returns {Promise<void>}
     */
    async initialize() {
      const startTime = context.performance?.now?.() || Date.now();

      // 🔒 CRITICAL: Prevent ANY re-initialization (during or after bootstrap)
      if (initialized) {
        const error =
          '❌ CRITICAL ARCHITECTURAL VIOLATION: initialize() called more than once! ' +
          'CoreEventBus must only be initialized a single time during the application lifecycle. ' +
          'Multiple initializations will corrupt event history and subscriber state. ' +
          'Stack trace:';
        isDev() && console.error(error, new Error().stack);
        throw new Error(error);
      }
      initialized = true; // Set flag immediately on first run

      // 🔒 CRITICAL: Additional check for post-bootstrap re-initialization
      if (bootstrapComplete) {
        const error =
          '❌ CRITICAL ARCHITECTURAL VIOLATION: initialize() called AFTER bootstrap complete! ' +
          'CoreEventBus can only be initialized ONCE during Phase 1 bootstrap. ' +
          'Multiple initializations violate event-driven architecture and will corrupt event history. ' +
          'Stack trace:';
        isDev() && console.error(error, new Error().stack);
        throw new Error(error);
      }

      // Clear any existing state
      this.clearEventHistory();

      // Milestone: State cleared
      this.publish(EVENT_TYPES.STATE_CLEARED, {
        timestamp: Date.now(),
        source: 'CoreEventBus.initialize',
      });

      // Wait for DOM to be ready
      if (!isDOMReady()) {
        await new Promise((resolve) => {
          context.document.addEventListener('DOMContentLoaded', resolve);
        });
      }

      // Process any queued UI events
      if (uiEventQueue && uiEventQueue.length > 0) {
        const queueLength = uiEventQueue.length;
        uiEventQueue.forEach(({ eventType, eventData }) => {
          this.publish(eventType, eventData);
        });
        uiEventQueue = [];

        // Milestone: UI queue processed
        this.publish(EVENT_TYPES.UI_QUEUE_PROCESSED, {
          queueLength,
          timestamp: Date.now(),
          source: 'CoreEventBus.initialize',
        });
      }

      // Check jQuery availability
      if (!isJQueryAvailable()) {
        this.publish(EVENT_TYPES.JQUERY_MISSING, { timestamp: Date.now() });
      }

      // Publish DOM ready event
      this.publish(EVENT_TYPES.DOM_READY, {
        timestamp: Date.now(),
        hasJQuery: isJQueryAvailable(),
      });

      // 🚀 Track initialization performance
      const executionTime =
        (context.performance?.now?.() || Date.now()) - startTime;
      this.publish(EVENT_TYPES.EVENT_BUS_INITIALIZED, {
        executionTime: executionTime.toFixed
          ? executionTime.toFixed(2)
          : executionTime,
        timestamp: Date.now(),
        subscriberCount: performanceMetrics.subscriberCount,
      });

      isDev() &&
        console.log(
          `✅ CoreEventBus initialized in ${executionTime.toFixed ? executionTime.toFixed(2) : executionTime}ms`
        );
    },

    /**
     * Subscribe to an event
     * @param {string} eventType - The event type to subscribe to
     * @param {function} callback - The callback to execute
     * @returns {function} Unsubscribe function
     */
    subscribe(eventType, callback) {
      validateEventType(eventType);

      // NEW: Warn about late subscriptions after bootstrap (unless system/performance events)
      if (
        bootstrapComplete &&
        !eventType.startsWith('system:') &&
        !eventType.startsWith('performance:')
      ) {
        isDev() &&
          console.warn(
            `⚠️ LATE SUBSCRIPTION: Subscribing to '${eventType}' after bootstrap complete. ` +
              `This may cause missed events during initialization. ` +
              `Modules should subscribe during their initialization phase (before bootstrap_complete event). ` +
              `Stack trace:`,
            new Error().stack
          );
      }

      if (!subscribers.has(eventType)) {
        subscribers.set(eventType, new Set());
      }

      const eventSubscribers = subscribers.get(eventType);
      if (eventSubscribers.has(callback)) {
        return () => this.unsubscribe(eventType, callback);
      }

      eventSubscribers.add(callback);
      performanceMetrics.subscriberCount++;

      return () => this.unsubscribe(eventType, callback);
    },

    /**
     * Unsubscribe from an event
     * @param {string} eventType - The event type to unsubscribe from
     * @param {function} callback - The callback to remove
     */
    unsubscribe(eventType, callback) {
      validateEventType(eventType);

      if (!subscribers.has(eventType)) {
        return;
      }

      const eventSubscribers = subscribers.get(eventType);
      eventSubscribers.delete(callback);
      performanceMetrics.subscriberCount--;

      if (eventSubscribers.size === 0) {
        subscribers.delete(eventType);
      }
    },

    /**
     * Publish an event
     * @param {string} eventType - The event type to publish
     * @param {*} data - The data to pass to subscribers
     * @param {Object} options - Additional options for event handling
     * @param {string} [options.source='system'] - Source of the event
     * @param {boolean} [options.batch=false] - Whether to batch this event
     * @param {number} [options.batchDelay=10] - Delay in ms for batched events
     */
    publish(eventType, eventData) {
      try {
        // Validate event type is a string FIRST
        if (typeof eventType !== 'string') {
          throw new Error('Event type must be a string');
        }

        // THEN validate the prefix using the dedicated function
        validateEventType(eventType);

        const now = Date.now();
        const startTime = context.performance?.now?.() || now;

        // We only need to check for the history clear event now
        const isHistoryClearEvent = eventType === EVENT_TYPES.HISTORY_CLEARED;

        // Pattern #18: Centralized Payload Freezing (DRY)
        // CoreEventBus is the SINGLE place that freezes ALL event payloads.
        // This eliminates 40+ per-module Object.freeze() calls.
        // Complements Pattern #15/20 (Conductor Single Publication Point):
        //   - Pattern #15/20: WHO publishes events → conductor (state events)
        //   - Pattern #18: HOW payloads are protected → CoreEventBus freezes centrally
        const eventDataWithMeta = Object.freeze({
          ...eventData,
          source: eventData?.source || 'system',
          timestamp: eventData?.timestamp || now,
        });

        if (eventType.startsWith('ui:') && !isDOMReady()) {
          uiEventQueue.push({ eventType, eventData: eventDataWithMeta });
          return true; // Successfully queued
        }

        // Add to history UNLESS it's the history clear event
        if (!isHistoryClearEvent) {
          eventHistory.push({ eventType, eventData: eventDataWithMeta });
          if (eventHistory.length > MAX_HISTORY_LENGTH) {
            eventHistory = eventHistory.slice(-MAX_HISTORY_LENGTH);
          }
        }

        // Execute callbacks
        executeCallbacks(eventType, eventDataWithMeta);

        // Track performance (performance events are skipped inside trackPerformance)
        trackPerformance(eventType, startTime, this);

        // Return true to indicate successful publication
        return true;
      } catch (error) {
        // Check if the error came from our validation
        if (
          error.message === 'Event type must be a string' ||
          error.message === 'Invalid event type prefix'
        ) {
          throw error; // Re-throw validation errors for tests to catch
        }

        // Handle other errors (e.g., from callbacks or performance tracking)
        if (!eventType?.startsWith(EVENT_TYPES.ERROR)) {
          this.publish(EVENT_TYPES.ERROR, {
            error: error.message, // Pass error message
            stack: error.stack, // Pass stack trace
            source: 'system',
            timestamp: Date.now(),
          });
        }

        // Return false to indicate publication failure
        return false;
      }
    },

    /**
     * Get event history
     * @returns {Array} Event history
     */
    getEventHistory() {
      return [...eventHistory];
    },

    /**
     * Clear event history
     * @param {string} [eventType] Optional event type to clear
     * @returns {Object} Summary of cleared events
     */
    clearEventHistory(eventType) {
      if (!eventType) {
        const count = eventHistory.length;
        eventHistory = [];
        return { clearedCount: count, remainingCount: 0 };
      }

      const originalLength = eventHistory.length;
      const filteredEvents = eventHistory.filter(
        (event) => !event.eventType.startsWith(eventType)
      );
      const clearedCount = originalLength - filteredEvents.length;
      eventHistory = filteredEvents;

      return {
        clearedCount,
        remainingCount: filteredEvents.length,
      };
    },

    /**
     * Get performance metrics with calculated averages and summaries
     * @returns {Object} Performance metrics
     */
    getPerformanceMetrics() {
      const metrics = {
        ...performanceMetrics,
        averageExecutionTimes: {},
        totalEvents: Object.values(performanceMetrics.eventCounts).reduce(
          (a, b) => a + b,
          0
        ),
        slowEventCount: performanceMetrics.slowEvents.length,
        highFrequencyEventCount: performanceMetrics.highFrequencyEvents.length,
        memoryUsagePercentage:
          (performanceMetrics.memoryUsage / performanceMetrics.maxMemoryUsage) *
          100,
      };

      // Calculate average execution times
      Object.entries(performanceMetrics.executionTimes).forEach(
        ([eventType, times]) => {
          metrics.averageExecutionTimes[eventType] =
            times.reduce((a, b) => a + b, 0) / times.length;
        }
      );

      return metrics;
    },

    /**
     * Get performance configuration settings
     * @returns {Object} Performance configuration
     */
    getPerformanceConfig() {
      return {
        maxEventRate: performanceMetrics.maxEventRate,
        maxExecutionTime: performanceMetrics.maxExecutionTime,
        maxMemoryUsage: performanceMetrics.maxMemoryUsage,
      };
    },

    /**
     * Update performance metrics configuration
     * @param {Object} config - New performance configuration
     * @param {number} [config.maxEventRate] - Maximum events per second threshold
     * @param {number} [config.maxExecutionTime] - Maximum execution time threshold in milliseconds
     * @param {number} [config.maxMemoryUsage] - Maximum memory usage threshold in bytes
     * @param {boolean} [config.resetMetrics=false] - Whether to reset existing metrics
     * @returns {Object} Updated performance configuration
     */
    updatePerformanceMetrics(config = {}, resetMetrics = false) {
      try {
        // Validate configuration inputs
        if (config.maxEventRate !== undefined) {
          if (
            typeof config.maxEventRate !== 'number' ||
            config.maxEventRate <= 0
          ) {
            throw new Error('maxEventRate must be a positive number');
          }
          performanceMetrics.maxEventRate = config.maxEventRate;
        }

        if (config.maxExecutionTime !== undefined) {
          if (
            typeof config.maxExecutionTime !== 'number' ||
            config.maxExecutionTime <= 0
          ) {
            throw new Error('maxExecutionTime must be a positive number');
          }
          performanceMetrics.maxExecutionTime = config.maxExecutionTime;
        }

        if (config.maxMemoryUsage !== undefined) {
          if (
            typeof config.maxMemoryUsage !== 'number' ||
            config.maxMemoryUsage <= 0
          ) {
            throw new Error('maxMemoryUsage must be a positive number');
          }
          performanceMetrics.maxMemoryUsage = config.maxMemoryUsage;
        }

        // Reset metrics if requested
        if (resetMetrics) {
          performanceMetrics.eventCounts = {};
          performanceMetrics.executionTimes = {};
          performanceMetrics.slowEvents = [];
          performanceMetrics.highFrequencyEvents = [];
          performanceMetrics.lastHighFrequencyCheck = Date.now();
          performanceMetrics.lastMetricsUpdate = Date.now();

          // Maintain subscriber count - don't reset this as it would break subscription tracking
        }

        // Publish an event to notify of configuration changes
        this.publish(EVENT_TYPES.CONFIG_UPDATED, {
          previousConfig: this.getPerformanceConfig(),
          newConfig: {
            maxEventRate: performanceMetrics.maxEventRate,
            maxExecutionTime: performanceMetrics.maxExecutionTime,
            maxMemoryUsage: performanceMetrics.maxMemoryUsage,
          },
          resetPerformed: resetMetrics,
          source: 'system',
          timestamp: Date.now(),
        });

        return this.getPerformanceConfig();
      } catch (error) {
        this.publish(EVENT_TYPES.ERROR, {
          error: error.message,
          stack: error.stack,
          source: 'system',
          context: 'updatePerformanceMetrics',
          timestamp: Date.now(),
        });

        // Return current config if update failed
        return this.getPerformanceConfig();
      }
    },

    /**
     * Mark bootstrap as complete and enable runtime protection
     * Called by data-pipeline after Layer 4 completes
     * Prevents double initialization and Phase 2 modules from re-initializing
     */
    markBootstrapComplete() {
      bootstrapComplete = true;

      this.publish(EVENT_TYPES.BOOTSTRAP_COMPLETE, {
        timestamp: Date.now(),
        subscriberCount: performanceMetrics.subscriberCount,
        eventHistoryCount: eventHistory.length,
        source: 'CoreEventBus.markBootstrapComplete',
      });

      isDev() &&
        console.log(
          '🎯 CoreEventBus: Bootstrap complete - runtime protection enabled ' +
            `(${performanceMetrics.subscriberCount} subscribers, ${eventHistory.length} events in history)`
        );
    },
  };

  return CoreEventBus;
};

// Export for different environments
if (typeof module !== 'undefined' && module.exports) {
  module.exports = createCoreEventBus;
} else {
  const context = typeof window !== 'undefined' ? window : global;
  context.MurphyDoorConfigurator = context.MurphyDoorConfigurator || {};
  context.MurphyDoorConfigurator.CoreEventBus = createCoreEventBus(context);
}
